// RE₿EL TOKEN — Whitepaper page (/whitepaper, /token/whitepaper).
// Self-contained: zero changes to existing pages, components or logic.
// Inspired by Apple / Stripe / Base / Render / Chainlink / Arbitrum aesthetics.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Coins, Brain, Network, Wallet, ShoppingBag, GraduationCap,
  Sparkles, BookOpen, Users, Trophy, Award, BarChart3, Bot,
  Eye, Newspaper, Bell, ChartLine, Repeat, Star, Crown,
  Shirt, Palette, Tag, MapPin, Globe, Youtube, Facebook, Twitter, Flame,
  ShieldCheck, FileText, Lock, ExternalLink, ArrowRight, ChevronDown, Download,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { tWP } from "../i18n/whitepaper";

const TOKEN_LOGO_URL =
  "https://res.cloudinary.com/dubfcngsr/image/upload/v1780856140/rebel/rebel_token_logo_circular.png";

const COLORS = {
  bg: "#0B0F19",
  gold: "#F5D76E",
  cyan: "#00E5FF",
  purple: "#A855F7",
};

const Section = ({ id, eyebrow, title, children, className = "" }) => (
  <section id={id} className={`relative mx-auto max-w-6xl px-6 py-24 md:py-32 ${className}`}>
    {eyebrow && (
      <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#F5D76E]/80">{eyebrow}</p>
    )}
    {title && (
      <h2 className="font-display text-4xl font-black tracking-tight md:text-5xl bg-gradient-to-r from-white via-white to-[#F5D76E]/85 bg-clip-text text-transparent">
        {title}
      </h2>
    )}
    <div className="mt-10">{children}</div>
  </section>
);

const Card = ({ icon: Icon, title, sub, accent = "#F5D76E", testid }) => (
  <div
    data-testid={testid}
    className="group relative rounded-2xl border border-white/10 bg-white/[0.025] p-5 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-white/25"
    style={{ boxShadow: `0 0 0 0 ${accent}00` }}
  >
    <div className="flex items-start gap-3">
      <div className="rounded-xl border p-2.5" style={{ background: `${accent}14`, borderColor: `${accent}33` }}>
        <Icon className="h-4 w-4" style={{ color: accent }} />
      </div>
      <div>
        <p className="text-sm font-bold text-white">{title}</p>
        {sub && <p className="mt-0.5 text-xs text-white/55">{sub}</p>}
      </div>
    </div>
  </div>
);

// Animated counter (counts up from 0 to target when mounted)
const Counter = ({ to, suffix = "", duration = 1400, format = (v) => v.toLocaleString() }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf, start;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      setV(Math.round(to * (0.2 + 0.8 * (1 - Math.pow(1 - p, 3)))));
      if (p < 1) raf = requestAnimationFrame(step);
      else setV(to);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <span>{format(v)}{suffix}</span>;
};

// Tokenomics donut (SVG, no library)
const TokenomicsDonut = ({ labels }) => {
  const slices = [
    { label: labels.d1, v: 60, color: "#F5D76E" },
    { label: labels.d2, v: 15, color: "#00E5FF" },
    { label: labels.d3, v: 10, color: "#A855F7" },
    { label: labels.d4, v: 10, color: "#22d3ee" },
    { label: labels.d5, v: 5, color: "#fb7185" },
  ];
  const radius = 90;
  const stroke = 24;
  const C = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="flex flex-col items-center gap-6 md:flex-row md:items-center md:gap-12">
      <svg width="240" height="240" viewBox="0 0 240 240" className="-rotate-90">
        <circle cx="120" cy="120" r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
        {slices.map((s) => {
          const len = (s.v / 100) * C;
          const dash = `${len} ${C - len}`;
          const el = (
            <circle key={s.label} cx="120" cy="120" r={radius} fill="none"
              stroke={s.color} strokeWidth={stroke}
              strokeDasharray={dash} strokeDashoffset={-offset}
              style={{ transition: "stroke-dashoffset 1.4s ease" }}
            />
          );
          offset += len;
          return el;
        })}
        <text x="120" y="118" textAnchor="middle" fill="#fff" fontWeight="900" fontSize="22" transform="rotate(90 120 120)">1B</text>
        <text x="120" y="138" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="11" transform="rotate(90 120 120)">{labels.supply}</text>
      </svg>
      <ul className="space-y-2">
        {slices.map((s) => (
          <li key={s.label} className="flex items-center gap-3" data-testid={`tk-slice-${s.label.toLowerCase()}`}>
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60 w-32">{s.label}</span>
            <span className="font-mono text-base font-black text-white">{s.v}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Market radial bars
const MarketBars = ({ labels }) => {
  const data = [
    { label: labels.m1, value: 15000, unit: "B", color: "#A855F7" },
    { label: labels.m2, value: 5000, unit: "B", color: "#F5D76E" },
    { label: labels.m3, value: 500, unit: "B", color: "#00E5FF" },
    { label: labels.m4, value: 250, unit: "B", color: "#22d3ee" },
  ];
  const max = Math.max(...data.map((d) => d.value));
  return (
    <div className="space-y-4">
      {data.map((d) => (
        <div key={d.label}>
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/65">{d.label}</span>
            <span className="font-mono text-base font-black text-white">
              ${d.value >= 1000 ? `${(d.value / 1000).toFixed(d.value % 1000 === 0 ? 0 : 1)}T` : `${d.value}${d.unit}`}
            </span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-white/[0.04]">
            <div
              className="h-full rounded-full transition-[width] duration-1000 ease-out"
              style={{ width: `${(d.value / max) * 100}%`, background: `linear-gradient(90deg, ${d.color}, ${d.color}88)` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default function WhitepaperPage() {
  const { lang } = useLanguage();
  const t = (k) => tWP(lang, k);

  return (
    <div className="min-h-screen text-white" style={{ background: COLORS.bg }} data-testid="whitepaper-page">
      {/* HERO */}
      <header className="relative overflow-hidden">
        {/* Animated glows */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 -left-32 h-[480px] w-[480px] rounded-full bg-[#00E5FF]/20 blur-3xl" />
          <div className="absolute -bottom-32 -right-32 h-[480px] w-[480px] rounded-full bg-[#A855F7]/20 blur-3xl" />
          <svg className="absolute inset-0 h-full w-full opacity-[0.05]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div className="relative mx-auto max-w-5xl px-6 pt-32 pb-28 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-white/45 hover:text-white" data-testid="wp-home">
            {t("wp_back")}
          </Link>
          <div className="mt-10 inline-flex h-32 w-32 items-center justify-center overflow-hidden rounded-full ring-2 ring-[#A855F7]/40 shadow-[0_0_60px_rgba(168,85,247,0.45),0_0_90px_rgba(245,215,110,0.18)]">
            <img
              src={TOKEN_LOGO_URL}
              alt="RE₿EL Token"
              loading="eager"
              decoding="async"
              className="h-full w-full object-cover"
              data-testid="wp-logo"
            />
          </div>
          <h1 className="mt-8 font-display text-5xl font-black tracking-tighter md:text-7xl">
            RE₿EL TOKEN <span className="text-[#F5D76E]">(R₿L)</span>
          </h1>
          <p className="mt-3 text-lg font-light text-white/75 md:text-xl">
            {t("wp_subtitle")}
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-sm text-white/55 md:text-base">
            {t("wp_desc")}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/shadow/tokens" data-testid="wp-buy-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#F5D76E] px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-black shadow-[0_8px_30px_rgba(245,215,110,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_44px_rgba(245,215,110,0.55)]">
              <Coins className="h-4 w-4" /> {t("wp_buy")}
            </Link>
            <a href="/whitepaper/rebel-token-whitepaper.pdf" download="rebel-token-whitepaper.pdf" data-testid="wp-pdf-btn"
              className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-white shadow-[0_8px_30px_rgba(249,115,22,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_44px_rgba(249,115,22,0.55)]"
              style={{ background: "#f97316" }}>
              <Download className="h-4 w-4" /> {t("wp_pdf")}
            </a>
            <a href="https://x.com/ShibaInuWorld_" target="_blank" rel="noreferrer" data-testid="wp-community-btn"
              className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-white/85 transition-all hover:-translate-y-0.5 hover:border-[#A855F7]/50 hover:text-white">
              <Users className="h-4 w-4" /> {t("wp_join")}
            </a>
          </div>
          <ChevronDown className="mx-auto mt-16 h-5 w-5 animate-bounce text-white/30" />
        </div>
      </header>

      {/* 1. EXECUTIVE SUMMARY */}
      <Section id="summary" eyebrow={t("s1_eyebrow")} title={t("s1_title")}>
        <div className="grid gap-10 md:grid-cols-2">
          <div className="text-base leading-relaxed text-white/70">
            <p>{t("s1_para")}</p>
            <ul className="mt-6 space-y-2">
              {[t("s1_b1"), t("s1_b2"), t("s1_b3"), t("s1_b4")].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00E5FF]" /> {x}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent p-6">
            <div className="grid grid-cols-2 gap-3">
              {[
                { i: GraduationCap, l: t("s1_p1") },
                { i: Brain, l: t("s1_p2") },
                { i: Network, l: t("s1_p3") },
                { i: Wallet, l: t("s1_p4") },
                { i: ShoppingBag, l: t("s1_p5") },
                { i: Coins, l: t("s1_p6") },
              ].map(({ i: I, l }) => (
                <div key={l} className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <I className="h-4 w-4 text-[#F5D76E]" />
                  <span className="text-xs font-bold uppercase tracking-wide">{l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* 2. MARKET OPPORTUNITY */}
      <Section id="market" eyebrow={t("s2_eyebrow")} title={t("s2_title")}>
        <MarketBars labels={{ m1: t("s2_m1"), m2: t("s2_m2"), m3: t("s2_m3"), m4: t("s2_m4") }} />
        <p className="mt-6 text-xs text-white/40">{t("s2_sources")}</p>
      </Section>

      {/* 3. ECOSYSTEM MAP */}
      <Section id="ecosystem" eyebrow={t("s3_eyebrow")} title={t("s3_title")}>
        <div className="flex flex-col items-center gap-4 md:flex-row md:flex-wrap md:justify-center">
          {[
            { i: GraduationCap, l: t("s3_n1"), c: "#F5D76E" },
            { i: Brain, l: t("s3_n2"), c: "#00E5FF" },
            { i: Network, l: t("s3_n3"), c: "#A855F7" },
            { i: Wallet, l: t("s3_n4"), c: "#22d3ee" },
            { i: ShoppingBag, l: t("s3_n5"), c: "#fb7185" },
            { i: Coins, l: t("s3_n6"), c: "#F5D76E" },
            { i: ChartLine, l: t("s3_n7"), c: "#0052ff" },
          ].map(({ i: I, l, c }, idx, arr) => (
            <React.Fragment key={l}>
              <div className="group flex w-full max-w-[200px] flex-col items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.025] p-4 transition-all hover:-translate-y-1"
                style={{ boxShadow: `0 0 0 0 ${c}00` }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = `0 8px 24px ${c}30`}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = "none"}
              >
                <div className="rounded-xl p-3" style={{ background: `${c}18`, border: `1px solid ${c}40` }}>
                  <I className="h-5 w-5" style={{ color: c }} />
                </div>
                <p className="text-center text-[11px] font-bold uppercase tracking-wider">{l}</p>
              </div>
              {idx < arr.length - 1 && <ArrowRight className="h-4 w-4 shrink-0 text-white/25 md:rotate-0 rotate-90" />}
            </React.Fragment>
          ))}
        </div>
        {/* Estado actual del ecosistema */}
        <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur" data-testid="wp-ecosystem-status">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00E5FF]/80">{t("s3_status_t")}</p>
          <ul className="mt-3 grid gap-2 md:grid-cols-2">
            {["s3_st1", "s3_st2", "s3_st3", "s3_st4", "s3_st5", "s3_st6"].map((k) => (
              <li key={k} className="flex items-start gap-2 text-xs text-white/65">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" /> {t(k)}
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* 4. ACADEMY */}
      <Section id="academy" eyebrow={t("s4_eyebrow")} title={t("s4_title")}>
        <div className="grid gap-3 md:grid-cols-3">
          {[
            { i: GraduationCap, t: t("s4_c1_t"), s: t("s4_c1_s") },
            { i: Sparkles, t: t("s4_c2_t"), s: t("s4_c2_s") },
            { i: Award, t: t("s4_c3_t"), s: t("s4_c3_s") },
            { i: BookOpen, t: t("s4_c4_t"), s: t("s4_c4_s") },
            { i: Trophy, t: t("s4_c5_t"), s: t("s4_c5_s") },
            { i: ShieldCheck, t: t("s4_c6_t"), s: t("s4_c6_s") },
            { i: BarChart3, t: t("s4_c7_t"), s: t("s4_c7_s") },
          ].map((c) => (
            <Card key={c.t} icon={c.i} title={c.t} sub={c.s} accent={COLORS.gold} />
          ))}
        </div>
      </Section>

      {/* 5. TRENDIA */}
      <Section id="trendia" eyebrow={t("s5_eyebrow")} title={t("s5_title")}>
        <div className="grid gap-3 md:grid-cols-3">
          {[
            { i: Brain, t: t("s5_c1") },
            { i: Eye, t: t("s5_c2") },
            { i: BarChart3, t: t("s5_c3") },
            { i: Newspaper, t: t("s5_c4") },
            { i: ChartLine, t: t("s5_c5") },
            { i: Bell, t: t("s5_c6") },
          ].map((c) => (<Card key={c.t} icon={c.i} title={c.t} accent={COLORS.cyan} />))}
        </div>
      </Section>

      {/* 6. SHADOW NETWORK */}
      <Section id="shadow" eyebrow={t("s6_eyebrow")} title={t("s6_title")}>
        <div className="grid gap-3 md:grid-cols-3">
          {[
            { i: Crown, t: t("s6_c1_t"), s: t("s6_c1_s") },
            { i: Lock, t: t("s6_c2_t"), s: t("s6_c2_s") },
            { i: Users, t: t("s6_c3_t"), s: t("s6_c3_s") },
            { i: Trophy, t: t("s6_c4_t"), s: t("s6_c4_s") },
            { i: Coins, t: t("s6_c5_t"), s: t("s6_c5_s") },
            { i: Star, t: t("s6_c6_t"), s: t("s6_c6_s") },
          ].map((c) => (<Card key={c.t} icon={c.i} title={c.t} sub={c.s} accent={COLORS.purple} />))}
        </div>
        <div className="mt-8 rounded-2xl border border-[#A855F7]/30 bg-[#A855F7]/[0.05] p-5">
          <p className="text-xs font-mono uppercase tracking-[0.22em] text-[#A855F7]">{t("s6_flow")}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2 font-mono text-sm">
            <span className="rounded-full border border-white/20 px-3 py-1">{t("s6_pays")}</span>
            <ArrowRight className="h-3 w-3 text-white/40" />
            <span className="rounded-full border border-[#F5D76E]/40 bg-[#F5D76E]/10 px-3 py-1 text-[#F5D76E]">{t("s6_analyst")}</span>
            <span className="text-white/40">+</span>
            <span className="rounded-full border border-[#00E5FF]/40 bg-[#00E5FF]/10 px-3 py-1 text-[#00E5FF]">{t("s6_platform")}</span>
          </div>
          <p className="mt-4 border-t border-white/10 pt-3 text-xs text-white/60" data-testid="wp-ranking-prizes">
            {t("s6_prizes")}
          </p>
        </div>
      </Section>

      {/* 7. TOPWALLET */}
      <Section id="topwallet" eyebrow={t("s7_eyebrow")} title={t("s7_title")}>
        <div className="grid gap-3 md:grid-cols-2">
          <Card icon={Wallet} title={t("s7_c1_t")} sub={t("s7_c1_s")} accent="#22d3ee" />
          <Card icon={Bot} title={t("s7_c2_t")} sub={t("s7_c2_s")} accent="#22d3ee" />
          <Card icon={Star} title={t("s7_c3_t")} sub={t("s7_c3_s")} accent="#22d3ee" />
          <Card icon={ShieldCheck} title={t("s7_c4_t")} sub={t("s7_c4_s")} accent="#22d3ee" />
        </div>
        <div className="mt-6 overflow-hidden rounded-2xl border border-white/10">
          <table className="w-full text-left text-xs">
            <thead className="bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/45">
              <tr>
                <th className="px-4 py-3">{t("s7_th1")}</th>
                <th className="px-4 py-3">{t("s7_th2")}</th>
                <th className="px-4 py-3">{t("s7_th3")}</th>
                <th className="px-4 py-3">{t("s7_th4")}</th>
              </tr>
            </thead>
            <tbody className="text-white/65">
              {[
                ["Ledger", t("s7_t1"), "9.8", "★★★★★"],
                ["MetaMask", t("s7_t2"), "8.2", "★★★★☆"],
                ["Phantom", t("s7_t3"), "8.7", "★★★★★"],
                ["Rabby", t("s7_t2"), "9.1", "★★★★★"],
              ].map((r) => (
                <tr key={r[0]} className="border-t border-white/5">
                  <td className="px-4 py-3 font-bold">{r[0]}</td>
                  <td className="px-4 py-3">{r[1]}</td>
                  <td className="px-4 py-3 text-emerald-300">{r[2]}</td>
                  <td className="px-4 py-3 text-[#F5D76E]">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-white/50" data-testid="wp-affiliates">{t("s7_affiliates")}</p>
      </Section>

      {/* 8. SHADOW MARKET */}
      <Section id="market-app" eyebrow={t("s8_eyebrow")} title={t("s8_title")}>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <Card icon={Shirt} title={t("s8_c1")} accent="#fb7185" />
          <Card icon={Palette} title={t("s8_c2")} accent="#fb7185" />
          <Card icon={FileText} title={t("s8_c3")} accent="#fb7185" />
          <Card icon={Tag} title={t("s8_c4")} accent="#fb7185" />
        </div>
      </Section>

      {/* 9. TOKENOMICS */}
      <Section id="tokenomics" eyebrow={t("s9_eyebrow")} title={t("s9_title")}>
        <TokenomicsDonut labels={{
          supply: t("s9_supply"),
          d1: t("s9_d1"), d2: t("s9_d2"), d3: t("s9_d3"), d4: t("s9_d4"), d5: t("s9_d5"),
        }} />

        {/* Distribución de cada pago PRO en R₿L — 75/20/5 */}
        <div className="mt-8 rounded-2xl border border-[#F5D76E]/25 bg-[#F5D76E]/[0.04] p-5 backdrop-blur" data-testid="wp-pro-split">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F5D76E]/80">{t("s9_split_t")}</p>
          <div className="mt-4 space-y-3">
            {[
              { v: 75, l: t("s9_split_1"), c: "#F5D76E" },
              { v: 20, l: t("s9_split_2"), c: "#34d399" },
              { v: 5, l: t("s9_split_3"), c: "#fb7185" },
            ].map((r) => (
              <div key={r.l}>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-white/70">{r.l}</span>
                  <span className="font-mono text-sm font-black" style={{ color: r.c }}>{r.v}%</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-white/[0.05]">
                  <div className="h-full rounded-full" style={{ width: `${r.v}%`, background: r.c }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Liquidez bloqueada — datos técnicos verificables */}
        <div
          className="mt-8 overflow-hidden rounded-2xl border bg-gradient-to-br from-emerald-500/[0.06] to-emerald-500/[0.02] p-5 backdrop-blur"
          style={{ borderColor: "rgba(99,153,34,0.45)" }}
          data-testid="wp-liquidity-lock"
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <div className="mb-1 inline-flex items-center gap-2 font-mono text-[10px] font-black uppercase tracking-[0.25em]" style={{ color: "#9bc864" }}>
                <span>🔒</span><span>{t("liq_lock_title")}</span>
              </div>
              <div className="text-sm font-bold text-white">{t("liq_lock_detail")}</div>
              <div className="mt-1 text-xs text-white/65">{t("liq_lock_until")}</div>
              <div className="mt-1 text-[11px] text-white/45">{t("liq_lock_verify")} · PinkLock</div>
            </div>
            <a
              href="https://www.pinksale.finance/pinklock/base/record/1044713"
              target="_blank" rel="noopener noreferrer"
              data-testid="wp-liquidity-lock-link"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border px-4 py-2 text-[11px] font-black uppercase tracking-[0.2em] text-white transition-all hover:-translate-y-0.5"
              style={{ borderColor: "rgba(99,153,34,0.55)", background: "rgba(99,153,34,0.10)" }}
            >
              {t("liq_lock_cta")} →
            </a>
          </div>
        </div>

        {/* Datos verificables on-chain — contratos reales en Base L2 */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur" data-testid="wp-contract-facts">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F5D76E]/80">{t("tok_facts_title")}</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {[
              { l: t("tok_contract_t"), a: "0xf9214b42c4c53791846E90066E52D7306adFACf5", h: "https://basescan.org/token/0xf9214b42c4c53791846E90066E52D7306adFACf5" },
              { l: t("tok_proof_t"), a: "0xB0F56c0f10386707D5936A79eD48e51Ac52741c3", h: "https://basescan.org/address/0xB0F56c0f10386707D5936A79eD48e51Ac52741c3" },
            ].map((c) => (
              <a key={c.a} href={c.h} target="_blank" rel="noopener noreferrer"
                className="group rounded-xl border border-white/10 bg-white/[0.02] p-4 transition-all hover:-translate-y-0.5 hover:border-[#F5D76E]/40">
                <p className="text-xs font-bold text-white">{c.l}</p>
                <p className="mt-1 break-all font-mono text-[11px] text-white/55 transition-colors group-hover:text-[#F5D76E]">{c.a}</p>
              </a>
            ))}
          </div>
          <p className="mt-3 text-xs text-white/55">{t("tok_supply_note")}</p>
          <p className="mt-1 text-xs text-white/45">
            <span className="font-bold text-white/65">{t("tok_pools_t")}:</span> {t("tok_pools_v")}
          </p>
          {/* Listings y exchanges */}
          <div className="mt-4 border-t border-white/10 pt-3" data-testid="wp-listings">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F5D76E]/80">{t("tok_listings_t")}</p>
            <ul className="mt-2 grid gap-1.5 md:grid-cols-2">
              {["tok_li1", "tok_li2", "tok_li3", "tok_li4"].map((k) => (
                <li key={k} className="text-xs text-white/60">• {t(k)}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* 10. TOKEN UTILITY */}
      <Section id="utility" eyebrow={t("s10_eyebrow")} title={t("s10_title")}>
        <div className="grid gap-3 md:grid-cols-4">
          {[
            { i: Crown, t: t("s10_c1"), s: t("s10_c1_s") }, { i: ShieldCheck, t: t("s10_c2") },
            { i: ShoppingBag, t: t("s10_c3") }, { i: Trophy, t: t("s10_c4") },
            { i: Sparkles, t: t("s10_c5") }, { i: Star, t: t("s10_c6") },
            { i: Lock, t: t("s10_c7") }, { i: Bell, t: t("s10_c8"), s: t("s10_c8_s") },
            { i: Flame, t: t("s10_c9"), s: t("s10_c9_s") }, { i: Repeat, t: t("s10_c10"), s: t("s10_c10_s") },
          ].map((c) => (<Card key={c.t} icon={c.i} title={c.t} sub={c.s} accent={COLORS.gold} />))}
        </div>
        {/* R₿L Vault — revenue sharing highlight */}
        <div className="mt-4 rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.04] p-5 backdrop-blur" data-testid="wp-vault">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-300/80">
            {t("s10_vault_t")} · REVENUE SHARING
          </p>
          <p className="mt-2 text-sm leading-relaxed text-white/70">{t("s10_vault_s")}</p>
        </div>
      </Section>

      {/* 11. ROADMAP */}
      <Section id="roadmap" eyebrow={t("s11_eyebrow")} title={t("s11_title")}>
        <div className="relative">
          <div className="absolute left-0 right-0 top-6 h-px bg-gradient-to-r from-transparent via-[#F5D76E]/40 to-transparent" />
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {[
              { n: "F1", t: t("s11_f1_t"), items: t("s11_f1"), st: "done" },
              { n: "F2", t: t("s11_f2_t"), items: t("s11_f2"), st: "done" },
              { n: "F3", t: t("s11_f3_t"), items: t("s11_f3"), st: "done" },
              { n: "F4", t: t("s11_f4_t"), items: t("s11_f4"), st: "done" },
              { n: "F5", t: t("s11_f5_t"), items: t("s11_f5"), st: "done" },
              { n: "F6", t: t("s11_f6_t"), items: t("s11_f6"), st: "progress" },
              { n: "F7", t: t("s11_f7_t"), items: t("s11_f7"), st: "soon" },
            ].map((c) => {
              const styles = {
                done: { border: "rgba(52,211,153,0.6)", color: "#34d399", label: `✓ ${t("s11_done")}` },
                progress: { border: "rgba(245,215,110,0.6)", color: "#F5D76E", label: `◌ ${t("s11_progress")}` },
                soon: { border: "rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.55)", label: `○ ${t("s11_soon")}` },
              }[c.st];
              return (
              <div key={c.n} className="relative flex flex-col items-center">
                <div
                  className="z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 bg-[#0B0F19] font-mono text-[11px] font-black"
                  style={{ borderColor: styles.border, color: styles.color }}
                  data-testid={`roadmap-${c.n}`}
                >
                  {c.n}
                </div>
                <p className="mt-3 text-center text-xs font-bold text-white">{c.t}</p>
                <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em]" style={{ color: styles.color }}>
                  {styles.label}
                </span>
                <ul className="mt-3 space-y-1 text-center">
                  {(Array.isArray(c.items) ? c.items : []).map((i) => <li key={i} className="text-xs text-white/70">{i}</li>)}
                </ul>
              </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* 16. LEARN-TO-EARN */}
      <Section id="learn-to-earn" eyebrow={t("l2e_eyebrow")} title={t("l2e_title")}>
        <p className="max-w-3xl text-base text-white/70 md:text-lg">{t("l2e_intro")}</p>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-[#F5D76E]/25 bg-[#F5D76E]/[0.04] p-6 backdrop-blur" data-testid="wp-l2e-rewards">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F5D76E]/80">{t("l2e_rewards_t")}</p>
            <ul className="mt-3 space-y-2 text-sm text-white/80">
              {[t("l2e_r1"), t("l2e_r2"), t("l2e_r3"), t("l2e_r4"), t("l2e_r5")].map((r) => (
                <li key={r} className="flex items-start gap-2"><GraduationCap className="mt-0.5 h-4 w-4 shrink-0 text-[#F5D76E]" />{r}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-orange-400/25 bg-orange-500/[0.05] p-6 backdrop-blur" data-testid="wp-l2e-distribution">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-300">{t("l2e_dist_t")}</p>
            <ul className="mt-3 space-y-2 text-sm text-white/80">
              <li className="flex items-start gap-2"><Wallet className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />{t("l2e_dist_1")}</li>
              <li className="flex items-start gap-2"><Flame className="mt-0.5 h-4 w-4 shrink-0 text-orange-400" />{t("l2e_dist_2")}</li>
            </ul>
          </div>
        </div>
        <div className="mt-6 rounded-2xl border border-violet-400/25 bg-violet-500/[0.04] p-6 backdrop-blur" data-testid="wp-l2e-anticheat">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-violet-300">{t("l2e_anti_t")}</p>
          <ul className="mt-3 grid gap-2 text-sm text-white/80 md:grid-cols-2">
            {[t("l2e_a1"), t("l2e_a2"), t("l2e_a3"), t("l2e_a4"), t("l2e_a5"), t("l2e_a6")].map((a) => (
              <li key={a} className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-violet-300" />{a}</li>
            ))}
          </ul>
        </div>
      </Section>

      {/* 17. RE₿EL KOMBAT */}
      <Section id="rebel-kombat" eyebrow={t("kombat_eyebrow")} title={t("kombat_title")}>
        <p className="max-w-3xl text-base text-white/70 md:text-lg">{t("kombat_intro")}</p>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-rose-400/25 bg-rose-500/[0.04] p-6 backdrop-blur" data-testid="wp-kombat-chars">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-rose-300">{t("kombat_chars_t")}</p>
            <ul className="mt-3 space-y-2 text-sm text-white/80">
              <li>{t("kombat_c1")}</li>
              <li>{t("kombat_c2")}</li>
              <li>{t("kombat_c3")}</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-[#F5D76E]/25 bg-[#F5D76E]/[0.04] p-6 backdrop-blur" data-testid="wp-kombat-rewards">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F5D76E]/80">{t("kombat_rew_t")}</p>
            <p className="mt-3 text-base font-bold text-white">{t("kombat_rew_v")}</p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-orange-300">{t("kombat_dist_t")}</p>
            <ul className="mt-2 space-y-1.5 text-sm text-white/80">
              <li className="flex items-start gap-2"><Wallet className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />{t("kombat_d1")}</li>
              <li className="flex items-start gap-2"><Flame className="mt-0.5 h-4 w-4 shrink-0 text-orange-400" />{t("kombat_d2")}</li>
            </ul>
          </div>
        </div>
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.025] p-6 backdrop-blur" data-testid="wp-kombat-limits">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">{t("kombat_limits_t")}</p>
          <ul className="mt-3 grid gap-2 text-sm text-white/80 md:grid-cols-3">
            <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />{t("kombat_l1")}</li>
            <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />{t("kombat_l2")}</li>
            <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />{t("kombat_l3")}</li>
          </ul>
        </div>
      </Section>

      {/* 18. BURN MECHANISMS */}
      <Section id="burn-mechanisms" eyebrow={t("burn5_eyebrow")} title={t("burn5_title")}>
        <p className="max-w-3xl text-base text-white/70 md:text-lg">{t("burn5_intro")}</p>
        <p className="mt-4 break-all font-mono text-[11px] text-orange-300/90" data-testid="wp-burn5-addr">{t("burn5_addr")}</p>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[
            { t: t("burn5_s1_t"), d: t("burn5_s1_d"), c: "#F5D76E" },
            { t: t("burn5_s2_t"), d: t("burn5_s2_d"), c: "#f97316" },
            { t: t("burn5_s3_t"), d: t("burn5_s3_d"), c: "#A855F7" },
            { t: t("burn5_s4_t"), d: t("burn5_s4_d"), c: "#fb7185" },
            { t: t("burn5_s5_t"), d: t("burn5_s5_d"), c: "#00E5FF" },
          ].map((b) => (
            <div key={b.t} className="rounded-2xl border p-5 backdrop-blur" style={{ background: `${b.c}0d`, borderColor: `${b.c}33` }} data-testid={`wp-burn5-${b.t.split(" ")[0]}`}>
              <div className="flex items-center gap-2">
                <Flame className="h-4 w-4" style={{ color: b.c }} />
                <p className="text-sm font-bold text-white">{b.t}</p>
              </div>
              <p className="mt-2 text-xs text-white/70">{b.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 12. TEAM */}
      <Section id="team" eyebrow={t("s12_eyebrow")} title={t("s12_title")}>
        <div className="grid gap-4 md:grid-cols-2">
          {[
            { n: "Peter Stanga", r: t("s12_founder"), d: t("s12_d1"), photo: "/team/peter-stanga.png" },
            { n: "Camilo Rodríguez", r: t("s12_ceo"), d: t("s12_d2"), photo: "/team/camilo-rodriguez.png" },
          ].map((p) => (
            <div key={p.n} className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent p-6 backdrop-blur">
              <div className="flex items-center gap-4">
                <img
                  src={p.photo}
                  alt={p.n}
                  loading="lazy"
                  className="h-14 w-14 rounded-full border border-[#F5D76E]/40 object-cover object-top"
                  data-testid={`wp-team-photo-${p.n.split(" ")[0].toLowerCase()}`}
                />
                <div>
                  <p className="text-lg font-bold">{p.n}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#F5D76E]/80">{p.r}</p>
                </div>
              </div>
              <p className="mt-3 text-sm text-white/55">{p.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 13. TOKEN METRICS */}
      <Section id="metrics" eyebrow={t("s13_eyebrow")} title={t("s13_title")}>
        <div className="grid gap-3 md:grid-cols-4">
          {[
            { l: t("s13_network"), v: "Base L2" },
            { l: t("s13_supply"), v: 1_000_000_000 },
            { l: t("s13_products"), v: 5 },
            { l: t("s13_core"), v: 5 },
          ].map((m) => (
            <div key={m.l} className="min-w-0 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.025] p-5" data-testid={`metric-${m.l.toLowerCase().replace(/\s/g,'-')}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">{m.l}</p>
              <p
                className="mt-2 font-display font-black leading-[1.1] tracking-tight text-white tabular-nums"
                style={{ fontSize: "clamp(0.95rem, 1.7vw, 1.45rem)", wordBreak: "break-word" }}
              >
                {typeof m.v === "number" ? <Counter to={m.v} /> : m.v}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* 14. OFFICIAL LINKS */}
      <Section id="links" eyebrow={t("s14_eyebrow")} title={t("s14_title")}>
        <div className="grid gap-3 md:grid-cols-3">
          {[
            { h: "https://rebelbtc.com", l: "rebelbtc.com", i: Globe },
            { h: "https://basescan.org/token/0xf9214b42c4c53791846E90066E52D7306adFACf5", l: "Basescan · R₿L", i: ShieldCheck },
            { h: "https://trendia.cloud", l: "trendia.cloud", i: Brain },
            { h: "/shadow", l: "Shadow Network", i: Network },
            { h: "/topwallet", l: "TopWallet", i: Wallet },
            { h: "https://www.facebook.com/share/1Gt7FdeagG/", l: "Facebook", i: Facebook },
            { h: "https://youtube.com/@satoshin_gmx", l: "YouTube", i: Youtube },
            { h: "https://x.com/ShibaInuWorld_", l: "X", i: Twitter },
          ].map((x) => (
            <a key={x.l} href={x.h} target={x.h.startsWith("http") ? "_blank" : "_self"} rel="noreferrer"
              className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.025] p-4 transition-all hover:-translate-y-0.5 hover:border-[#F5D76E]/40">
              <span className="inline-flex items-center gap-3"><x.i className="h-4 w-4 text-[#F5D76E]" /><span className="text-sm font-bold">{x.l}</span></span>
              <ExternalLink className="h-3.5 w-3.5 text-white/40 transition-transform group-hover:translate-x-0.5" />
            </a>
          ))}
        </div>
      </Section>

      {/* 15. LEGAL */}
      <Section id="legal" eyebrow={t("s15_eyebrow")} title={t("s15_title")}>
        {/* Trust signal · liquidity lock */}
        <a
          href="https://www.pinksale.finance/pinklock/base/record/1044713"
          target="_blank" rel="noopener noreferrer"
          data-testid="legal-liquidity-lock-trust"
          className="mb-6 flex items-center justify-between gap-4 rounded-2xl border p-4 transition-all hover:-translate-y-0.5"
          style={{ borderColor: "rgba(99,153,34,0.45)", background: "rgba(99,153,34,0.06)" }}
        >
          <span className="text-sm font-bold text-white">{t("sec_lock_trust")}</span>
          <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "#9bc864" }}>PinkLock →</span>
        </a>
        <div className="grid gap-4 md:grid-cols-2">
          {[
            { t: t("s15_risk_t"), icon: ShieldCheck, c: "#F5D76E", body: t("s15_risk_b") },
            { t: t("s15_privacy_t"), icon: Lock, c: "#00E5FF", body: t("s15_privacy_b") },
            { t: t("s15_tos_t"), icon: FileText, c: "#A855F7", body: t("s15_tos_b") },
            { t: t("s15_token_t"), icon: Coins, c: "#fb7185", body: t("s15_token_b") },
          ].map((x) => (
            <div
              key={x.t}
              className="rounded-2xl border bg-white/[0.02] p-5 backdrop-blur transition-colors hover:bg-white/[0.04]"
              style={{ borderColor: `${x.c}33` }}
              data-testid={`legal-${x.t.toLowerCase().replace(/\s/g,'-')}`}
            >
              <div className="flex items-center gap-2.5">
                <span
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border"
                  style={{ background: `${x.c}14`, borderColor: `${x.c}55`, color: x.c }}
                >
                  <x.icon className="h-3.5 w-3.5" />
                </span>
                <p className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: x.c }}>{x.t}</p>
              </div>
              <p className="mt-3 text-[13px] leading-relaxed text-white/65">{x.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 max-w-3xl text-xs leading-relaxed text-white/45">
          {t("s15_legal_note")} © {new Date().getFullYear()} RE₿EL.
        </p>
      </Section>

      {/* FOOTER */}
      <footer className="relative border-t border-white/5 px-6 py-12 text-center">
        <p className="font-display text-3xl font-black tracking-tighter">RE₿EL</p>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
          {t("foot_motto")}
        </p>
      </footer>
    </div>
  );
}
